/**
 * Tipos del documento enriquecido.
 *
 * El formato es JSON compatible con ProseMirror/TipTap porque es el que ya
 * produce y consume el editor del admin sin capa de traduccion intermedia.
 * El renderer de este paquete (`render.ts`) es el unico que convierte este
 * arbol a HTML, tanto para la web publica como para el preview del admin.
 */

export interface RichMark {
  type: string;
  attrs?: Record<string, unknown>;
}

export interface RichNode {
  type: string;
  attrs?: Record<string, unknown>;
  content?: RichNode[];
  marks?: RichMark[];
  text?: string;
}

export interface RichDoc extends RichNode {
  type: "doc";
  content: RichNode[];
}

/**
 * Envoltura que se persiste en `blog_posts.content` (jsonb).
 *
 * `body` se mantiene escrito junto a `doc` a proposito: es la clave que ya
 * leen los readers legacy (`content.body: string[]`). Asi una publicacion
 * nueva sigue siendo legible por codigo viejo, y una vieja sigue siendo
 * legible por el codigo nuevo (que normaliza `body` -> `doc`).
 */
export interface RichContent {
  version: number;
  doc: RichDoc;
  /** Parrafos planos derivados del doc. Solo lectura de compatibilidad. */
  body: string[];
}

export interface TocEntry {
  /** 2 o 3. El H1 lo genera el titulo de la publicacion, nunca el contenido. */
  level: 2 | 3;
  text: string;
  anchor: string;
}

export interface HeadingRef {
  level: 2 | 3;
  text: string;
  anchor: string;
}

export interface FaqEntry {
  question: string;
  answer: string;
}

export interface ReadingStats {
  words: number;
  minutes: number;
}

export interface ImageAudit {
  total: number;
  withoutAlt: number;
}

/** Nodos de bloque que el renderer soporta. Cualquier otro se descarta. */
export const BLOCK_NODE_TYPES = [
  "doc",
  "paragraph",
  "heading",
  "bulletList",
  "orderedList",
  "listItem",
  "blockquote",
  "horizontalRule",
  "codeBlock",
  "table",
  "tableRow",
  "tableHeader",
  "tableCell",
  "image",
  "hardBreak",
  "faqList",
  "faqItem",
] as const;

/** Marcas inline que el renderer soporta. Cualquier otra se descarta. */
export const MARK_TYPES = ["bold", "italic", "underline", "strike", "code", "link"] as const;

export type BlockNodeType = (typeof BLOCK_NODE_TYPES)[number];
export type MarkType = (typeof MARK_TYPES)[number];

/** Version del formato que escribe el admin actual. */
export const RICH_CONTENT_VERSION = 2;
